

import { Component, Vue, Watch, Prop } from "vue-property-decorator"
import { namespace, Getter, Action } from "vuex-class"

// @ts-ignore
import cloneDeep from "lodash/cloneDeep"

import { DateTime } from "luxon"

// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"

import { Format, Layout, LayoutType } from "@/types/format"
import StringValidation from "@/scripts/stringValidation"

import SkeletonLoader from "@/components/SkeletonLoader.vue"
import DataTable from "../DataTable.vue"
import UserImage from "../../UserImage.vue"
import FormatSectionSetup from "./FormatSectionSetup.vue"

import formatApi from "@/services/api/Formatting"
import { Field, TimeZone } from "@/types"
import { CaseColumn } from "@/types/caseColumns"
import api from "@/services/api"

const setup = namespace("setup")

@Component({
	components: {
		"scrollbar": VuePerfectScrollbar,
		"data-table": DataTable,
		"skeleton": SkeletonLoader,
		"user-image": UserImage
	}
})
export default class FormatSetup extends Vue {
    @setup.State fields!: Field[]

	@Getter getTenantTimezones!: TimeZone[]

	private caseNumber: string | null = null
	private loading = false
	private downloading = false

	editItem: Format | null = null

	private layouts: Layout[] = []
	private formats: Format[] = []

	private dateFormats = [
		{ id: "dddd, MMM, d, yyyy", title: this.dateInFormat("EEEE, MMM, d, yyyy") },
		{ id: "dddd d MMM yyyy", title: this.dateInFormat("EEEE d MMM yyyy") },
		{ id: "d MMM yyyy", title: this.dateInFormat("d MMM yyyy") },
		{ id: "MM/dd/yyyy", title: this.dateInFormat("MM/dd/yyyy") },
		{ id: "dd/MM/yyyy", title: this.dateInFormat("dd/MM/yyyy") }
	]

	private get columns () {
		return [
			{
				key: "title",
				label: "Title",
				errors: (item: Format) => StringValidation.validateTitle(item.title)
			},
			{
				key: "layout",
				label: "Layout",
				hasTemplate: true,
				dataType: "list",
				options: this.layouts,
				listLabel: "title",
				errors: (item: any) => item.layout ? null : "Please select a layout"
			},
			{
				key: "createdBy.name",
				label: "Created By",
				inModal: (item: any) => false,
				hasTemplate: true
			},
			{
				key: "sections",
				label: "Sections",
				inTable: false,
				dynamicComponent: FormatSectionSetup,
				componentProperties: {
					"additionalFields": this.templateAdditionalFields,
					fields: this.templateFields,
					layout: this.editItem && this.editItem.layout ? this.layoutsByKey[this.editItem.layout] : null
				}
			},
			{
				key: "dateFormat",
				label: "Date Format",
				dataType: "list",
				options: this.dateFormats,
				inTable: false,
				errors: (item: any) => !item.dateFormat ? "Please select a date format" : null
			},
			{
				key: "timeZone",
				label: "Timezone",
				dataType: "list",
				options: this.getTenantTimezones,
				inTable: false,
				errors: (item: any) => !item.timeZone ? "Please select a date timezone" : null
			}
		]
	}

	private get layoutsByKey (): { [key: string]: Layout } {
		return this.layouts.toDictionary("id")
	}

	private get templateFields (): CaseColumn[] {
		return this.fields.filter(f => f.dataType && f.dataType !== "file-only")
			.map(f => {
				return {
					label: f.title,
					key: "fields." + f.id,
					fieldId: f.id,
					dataType: f.dataType,
					dataSourceId: f.dataSourceId,
					description: f.description,
					dynamicField: true,
					fieldExtensions: f.extensions
				} as CaseColumn
			})
	}

	private get templateAdditionalFields (): { id: string, label: string }[] {
		return [
			{ id: "recipientName", label: "Recipient Name" },
			{ id: "recordLink", label: "Link to Record" },
			{ id: "notificationDate", label: "Notification Date" }
		]
	}

	async mounted () {
		this.loading = true
		await Promise.all([
			formatApi.formatGet().then(formats => this.formats = formats),
			formatApi.layoutsGet().then(layouts => this.layouts = layouts)
		])
		this.loading = false
	}

	public async saveFormat (format: Format) {
		const layout = this.layouts.firstOrDefault(l => l.id === format.layout)
		if (layout) {
			// Filter out sections that aren't relevant for the current layout
			const layoutSections = new Set(layout.sections.map(s => s.key))
			format.sections = format.sections.filter(f => layoutSections.has(f.layoutSection))
		}

		// Save and update table
		let newFormat = await formatApi.formatSave(format)
		if (format.id) {
			const existingIndex = this.formats.findIndex(f => f.id === newFormat.id)
			if (existingIndex) {
				Vue.set(this.formats, existingIndex, newFormat)
			}
		} else {
			this.formats.push(newFormat)
		}
	}

	createNewItem () {
		this.editItem = {
			id: null,
			title: "",
			layout: null,
			layoutType: LayoutType.Email,
			timeZone: "",
			dateFormat: "",
			sections: []
		}
		return this.editItem
	}

	setEditItem (editItem: Format) {
		this.editItem = cloneDeep(editItem)
		return this.editItem
	}

	dateInFormat (format: string): string {
		return DateTime.local().toFormat(format)
	}

	async deleteFormat (format: Format) {
		await formatApi.formatDelete(format)
		const existingIndex = this.formats.findIndex(f => f.id === format.id)
		if (existingIndex) {
			this.formats.splice(existingIndex, 1)
		}
	}
}

